// src/pages/ShipmentReceive.jsx
import React, { useMemo, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../services/firebase";
import { recordInventoryMove } from "../services/stockMoves";

const DEFAULT_LOCATION_ID = "boutique";
const CURRENCY = "XOF";
const stockKey = (locId, itemId) => `${locId}__${itemId}`;

function rid() {
  let c;
  if (typeof window !== "undefined" && window.crypto) c = window.crypto;
  else if (typeof global !== "undefined" && global.crypto) c = global.crypto;

  if (c && typeof c.randomUUID === "function") return c.randomUUID();

  const t =
    typeof performance !== "undefined" && performance.now
      ? performance.now()
      : Date.now();
  return `${Math.floor(t).toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
}

export default function ShipmentReceive() {
  const [locationId, setLocationId] = useState(DEFAULT_LOCATION_ID);

  // Recherche / chargement d’un shipment
  const [codeOrId, setCodeOrId] = useState("");
  const [ship, setShip] = useState(null); // {id, code, lines: [...]}
  const [lines, setLines] = useState([]); // lignes enrichies : [{rowId,itemId,name,barcode,unitCost,shippedQty,rcvdQty,qtyToReceive,poId}]
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // ------- Chargement du shipment + itemsEK -------
  async function loadShipment() {
    setMsg("");
    setShip(null);
    setLines([]);
    const t = codeOrId.trim();
    if (!t) return;

    try {
      setLoading(true);
      let snap = null;

      // 1) Essayer par docId direct
      const direct = await getDoc(doc(db, "shipments", t));
      if (direct.exists()) {
        snap = direct;
      } else {
        // 2) Chercher par code (champ "code": "SHP-...")
        const qy = query(
          collection(db, "shipments"),
          where("code", "==", t),
          limit(1)
        );
        const qs = await getDocs(qy);
        if (!qs.empty) snap = qs.docs[0];
      }

      if (!snap) {
        setMsg("Expédition introuvable.");
        return;
      }

      const data = snap.data() || {};
      const src = Array.isArray(data.lines) ? data.lines : [];

      // 🔥 On enrichit avec itemsEK
      const itemIds = Array.from(
        new Set(src.map((l) => l.itemId).filter(Boolean))
      );

      const itemsById = new Map();
      const chunkSize = 10;
      for (let i = 0; i < itemIds.length; i += chunkSize) {
        const slice = itemIds.slice(i, i + chunkSize);
        const qItems = query(
          collection(db, "itemsEK"),
          where("__name__", "in", slice)
        );
        const snapItems = await getDocs(qItems);
        snapItems.forEach((d) => {
          itemsById.set(d.id, d.data());
        });
      }

      const mapped = src.map((l) => {
        const base = itemsById.get(l.itemId) || {};
        const shipped = Number(l.qty || 0);
        const already = Number(l.rcvdQty || 0);

        return {
          rowId: rid(),
          itemId: l.itemId,
          name: l.name || base.name || base.title || "Article",
          barcode: l.barcode || base.barcode || "",
          unitCost: Number(
            l.unitCost != null
              ? l.unitCost
              : base.cost != null
              ? base.cost
              : 0
          ),
          shippedQty: shipped,
          rcvdQty: already,
          qtyToReceive: Math.max(0, shipped - already),
          poId: l.poId || "",
        };
      });

      setShip({ id: snap.id, ...data });
      setLines(mapped);
    } catch (e) {
      console.error(e);
      setMsg("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  }

  // ------- TOTAUX UI -------
  const totals = useMemo(() => {
    const qty = lines.reduce((s, r) => s + Number(r.qtyToReceive || 0), 0);
    const cost = lines.reduce(
      (s, r) => s + Number(r.qtyToReceive || 0) * Number(r.unitCost || 0),
      0
    );
    return { qty, cost };
  }, [lines]);

  function remain(line) {
    return Math.max(
      0,
      Number(line.shippedQty || 0) - Number(line.rcvdQty || 0)
    );
  }

  // ------- Transaction de réception -------
  async function handleReceive() {
    setMsg("");
    if (!ship?.id) {
      setMsg("Charge d’abord une expédition.");
      return;
    }

    const chosen = lines.filter((l) => Number(l.qtyToReceive || 0) > 0);
    if (!chosen.length) {
      setMsg("Aucune quantité à réceptionner.");
      return;
    }

    // validations simples côté client
    for (const l of chosen) {
      const max = remain(l);
      if (Number(l.qtyToReceive) > max) {
        setMsg(
          `Quantité à recevoir trop élevée pour ${l.name} (max ${max}).`
        );
        return;
      }
    }

    try {
      setLoading(true);

      await runTransaction(db, async (tx) => {
        // === 1) LECTURES ===

        // Shipment
        const shipRef = doc(db, "shipments", ship.id);
        const shipSnap = await tx.get(shipRef);
        if (!shipSnap.exists())
          throw new Error("Shipment introuvable pendant la transaction.");
        const shipData = shipSnap.data() || {};
        const shipLines = Array.isArray(shipData.lines) ? shipData.lines : [];

        // Index : itemId + poId → ligne du shipment
        const slIndex = new Map();
        shipLines.forEach((l, i) => {
          const key = `${l.itemId}__${l.poId || ""}`;
          slIndex.set(key, { line: l, idx: i });
        });

        // StockAgg / Stock
        const itemIds = Array.from(new Set(chosen.map((l) => l.itemId)));
        const aggRefs = itemIds.map((id) => doc(db, "stockAgg", id));
        const stockRefs = itemIds.map((id) =>
          doc(db, "stock", stockKey(locationId, id))
        );
        const aggSnaps = await Promise.all(aggRefs.map((r) => tx.get(r)));
        const stockSnaps = await Promise.all(stockRefs.map((r) => tx.get(r)));

        // PO impactés
        const poIds = Array.from(
          new Set(chosen.filter((l) => l.poId).map((l) => String(l.poId)))
        );
        const poRefs = poIds.map((id) => doc(db, "purchaseOrders", id));
        const poSnaps = await Promise.all(poRefs.map((r) => tx.get(r)));

        // === 2) VALIDATIONS ===

        // 2.a. Shipment : ne pas dépasser qty-expédiée - déjà reçu
        for (const l of chosen) {
          const key = `${l.itemId}__${l.poId || ""}`;
          const found = slIndex.get(key);
          if (!found)
            throw new Error(
              `Ligne d’expédition introuvable pour ${l.name}`
            );
          const line = found.line;
          const shipped = Number(line.qty || 0);
          const already = Number(line.rcvdQty || 0);
          const max = Math.max(0, shipped - already);
          if (Number(l.qtyToReceive || 0) > max) {
            throw new Error(
              `Réception > restant expédié pour ${l.name} (reste ${max}).`
            );
          }
        }

        // 2.b. PO : ne pas dépasser le restant à recevoir sur le PO
        const poById = new Map();
        for (let i = 0; i < poRefs.length; i++) {
          const ref = poRefs[i];
          const snap = poSnaps[i];
          if (!snap.exists())
            throw new Error(`PO introuvable (${ref.id}).`);
          const pdata = snap.data() || {};
          const items = Array.isArray(pdata.items) ? pdata.items : [];
          const idx = new Map();
          items.forEach((it) => it?.itemId && idx.set(it.itemId, it));
          poById.set(ref.id, { ref, data: pdata, items, idx });
        }

        for (const l of chosen) {
          if (!l.poId) continue;
          const x = poById.get(String(l.poId));
          if (!x) throw new Error(`PO ${l.poId} introuvable.`);
          const poLine = x.idx.get(l.itemId);
          if (!poLine)
            throw new Error(
              `Ligne PO manquante (${l.itemId}) dans PO ${l.poId}`
            );
          const qOrd = Number(poLine.qtyOrdered || 0);
          const qRec = Number(poLine.qtyReceived || 0);
          const remainPO = Math.max(0, qOrd - qRec);
          if (Number(l.qtyToReceive) > remainPO) {
            throw new Error(
              `Réception dépasse restant du PO ${l.poId} pour ${
                poLine.name || l.itemId
              } (reste ${remainPO}).`
            );
          }
        }

        // === 3) CALCULS en mémoire ===

        // 3.a. New stockAgg + stock
        const newAgg = new Map();
        const newStock = new Map();

        for (let i = 0; i < itemIds.length; i++) {
          const itemId = itemIds[i];
          const aggSnap = aggSnaps[i];
          const stockSnap = stockSnaps[i];

          const currentAgg = aggSnap.exists()
            ? aggSnap.data()
            : { onOrder: 0, total: 0, perLocation: {} };
          const perLoc = { ...(currentAgg.perLocation || {}) };
          const curLoc = Number(perLoc[locationId] || 0);
          const curTotal = Number(currentAgg.total || 0);
          const curOnOrder = Number(currentAgg.onOrder || 0);

          const addQty = chosen
            .filter((x) => x.itemId === itemId)
            .reduce((s, x) => s + Number(x.qtyToReceive || 0), 0);

          const addOnOrder = chosen
            .filter((x) => x.itemId === itemId && x.poId)
            .reduce((s, x) => s + Number(x.qtyToReceive || 0), 0);

          perLoc[locationId] = curLoc + addQty;

          newAgg.set(itemId, {
            onOrder: Math.max(0, curOnOrder - addOnOrder),
            total: curTotal + addQty,
            perLocation: perLoc,
          });

          const curQty =
            (stockSnap.exists() ? stockSnap.data().qty : 0) || 0;
          newStock.set(itemId, curQty + addQty);
        }

        // 3.b. Lignes du shipment : rcvdQty += qtyToReceive
        const newShipLines = [...shipLines];
        chosen.forEach((l) => {
          const key = `${l.itemId}__${l.poId || ""}`;
          const { idx } = slIndex.get(key);
          const old = newShipLines[idx];
          const rcvd =
            Number(old.rcvdQty || 0) + Number(l.qtyToReceive || 0);
          newShipLines[idx] = { ...old, rcvdQty: rcvd };
        });

        let allReceived = true;
        for (const L of newShipLines) {
          const shipped = Number(L.qty || 0);
          const rcvd = Number(L.rcvdQty || 0);
          if (rcvd < shipped) {
            allReceived = false;
            break;
          }
        }
        const newShipStatus = allReceived ? "received" : "partial";

        // 3.c. PO : qtyReceived + statut
        for (const [poId, poInfo] of poById) {
          const { items, idx } = poInfo;
          const addForThisPO = chosen.filter(
            (l) => String(l.poId) === poId
          );
          if (!addForThisPO.length) continue;

          addForThisPO.forEach((ln) => {
            const L = idx.get(ln.itemId);
            if (!L) return;
            L.qtyReceived =
              Number(L.qtyReceived || 0) +
              Number(ln.qtyToReceive || 0);
          });

          let poAll = true;
          let poAny = false;
          for (const it of items) {
            const qO = Number(it.qtyOrdered || 0);
            const qR = Number(it.qtyReceived || 0);
            if (qO > qR) poAll = false;
            if (qR > 0) poAny = true;
          }
          poInfo.newStatus = poAll
            ? "closed"
            : poAny
            ? "partial"
            : "open";
        }

        // === 4) ÉCRITURES ===

        // 4.1 stockAgg + stock
        for (let i = 0; i < itemIds.length; i++) {
          const itemId = itemIds[i];
          const aggRef = doc(db, "stockAgg", itemId);
          const sRef = doc(db, "stock", stockKey(locationId, itemId));
          const aggVals = newAgg.get(itemId);
          const qtyLoc = newStock.get(itemId);

          tx.set(
            aggRef,
            {
              onOrder: aggVals.onOrder,
              total: aggVals.total,
              perLocation: aggVals.perLocation,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );

          tx.set(
            sRef,
            {
              locationId,
              itemId,
              qty: qtyLoc,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }

        // 4.2 PO impactés
        for (const [, poInfo] of poById) {
          tx.update(poInfo.ref, {
            items: poInfo.items,
            status: poInfo.newStatus,
            updatedAt: serverTimestamp(),
          });
        }

        // 4.3 Shipment + Receipt
        const shipRef2 = doc(db, "shipments", ship.id);
        const receiptRef = doc(collection(db, "receipts"));

        tx.update(shipRef2, {
          lines: newShipLines,
          status: newShipStatus,
          receivedAt: serverTimestamp(),
          receivedLocation: locationId,
          updatedAt: serverTimestamp(),
        });

        tx.set(receiptRef, {
          type: "shipment_receive",
          shipmentId: ship.id,
          locationId,
          items: chosen.map((l) => ({
            itemId: l.itemId,
            qty: Number(l.qtyToReceive || 0),
            unitCost: Number(l.unitCost || 0),
            poId: l.poId || null,
          })),
          at: serverTimestamp(),
        });
      });

      // 🔥 Journaliser les mouvements pour la fiche de stock
      await Promise.all(
        chosen.map((l) =>
          recordInventoryMove({
            itemId: l.itemId,
            locationId,
            type: "reception",
            delta: Number(l.qtyToReceive || 0),
            countedQty: null,
            note: `Réception shipping ${ship.code || ship.id}`,
            ref: ship.id,
            extra: {
              shipmentId: ship.id,
              shipmentCode: ship.code || null,
              poId: l.poId || null,
              unitCost: Number(l.unitCost || 0),
            },
          })
        )
      );

      // Recharger le shipping pour voir le restant
      await loadShipment();
      setMsg("✅ Réception enregistrée.");
    } catch (e) {
      console.error(e);
      setMsg("❌ " + e.message);
    } finally {
      setLoading(false);
    }
  }

  // ------- Rendu -------
  return (
    <div className="container py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0">Réception d’un Shipping</h3>
        <div className="d-flex gap-2">
          <input
            className="form-control"
            style={{ width: 260 }}
            placeholder="Shipment ID ou Code (ex: SHP-...)"
            value={codeOrId}
            onChange={(e) => setCodeOrId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadShipment()}
          />
          <button
            className="btn btn-outline-secondary"
            onClick={loadShipment}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Chargement…
              </>
            ) : (
              "Charger"
            )}
          </button>
        </div>
      </div>

      {/* Métadonnées + emplacement */}
      <div className="card mb-3">
        <div className="card-body row g-3">
          <div className="col-12 col-md-4">
            <label className="form-label">Emplacement de réception</label>
            <input
              className="form-control"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              placeholder="ex: boutique"
            />
          </div>
          <div className="col-12 col-md-8">
            {ship ? (
              <div className="p-2 border rounded small">
                <div className="fw-semibold">
                  Shipment: <code>{ship.id}</code>{" "}
                  {ship.code ? `· ${ship.code}` : ""}
                </div>
                <div>
                  Statut :{" "}
                  <strong>{ship.status || "in_transit"}</strong> · Origine :{" "}
                  {ship.origin || "—"} → Destination :{" "}
                  {ship.destination || "—"}
                </div>
                {(ship.carrier || ship.tracking) && (
                  <div className="text-muted">
                    {ship.carrier || "—"}{" "}
                    {ship.tracking ? `· ${ship.tracking}` : ""}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted small">Aucun shipping chargé.</div>
            )}
          </div>
        </div>
      </div>

      {/* Lignes à recevoir */}
      <div className="card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>Article</th>
                  <th>CB</th>
                  <th className="text-end">Expédié</th>
                  <th className="text-end">Déjà reçu</th>
                  <th className="text-end">Reste</th>
                  <th className="text-end" style={{ width: 140 }}>
                    À recevoir
                  </th>
                  <th className="text-end">
                    Coût ({CURRENCY})
                  </th>
                  <th className="text-end">
                    Total ({CURRENCY})
                  </th>
                  <th>PO</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-muted">
                      Aucune ligne
                    </td>
                  </tr>
                ) : (
                  lines.map((l) => {
                    const rest = remain(l);
                    const q = Number(l.qtyToReceive || 0);
                    const over = q > rest;
                    const total =
                      q * Number(l.unitCost || 0);
                    return (
                      <tr
                        key={l.rowId}
                        className={over ? "table-danger" : ""}
                      >
                        <td>
                          <div className="fw-semibold">
                            {l.name}
                          </div>
                          <div className="small text-muted">
                            {l.itemId}
                          </div>
                        </td>
                        <td>{l.barcode || "—"}</td>
                        <td className="text-end">
                          {Number(
                            l.shippedQty || 0
                          ).toLocaleString()}
                        </td>
                        <td className="text-end">
                          {Number(
                            l.rcvdQty || 0
                          ).toLocaleString()}
                        </td>
                        <td className="text-end fw-semibold">
                          {rest.toLocaleString()}
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max={rest}
                            className="form-control text-end"
                            value={l.qtyToReceive}
                            onChange={(e) =>
                              setLines((prev) =>
                                prev.map((x) =>
                                  x.rowId === l.rowId
                                    ? {
                                        ...x,
                                        qtyToReceive: Number(
                                          e.target.value || 0
                                        ),
                                      }
                                    : x
                                )
                              )
                            }
                          />
                        </td>
                        <td className="text-end">
                          {Number(
                            l.unitCost || 0
                          ).toLocaleString()}
                        </td>
                        <td className="text-end fw-semibold">
                          {total.toLocaleString()}
                        </td>
                        <td className="small">
                          {l.poId || (
                            <span className="text-muted">
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} className="text-end">
                    Totaux
                  </td>
                  <td className="text-end fw-bold">
                    {totals.qty.toLocaleString()}
                  </td>
                  <td></td>
                  <td className="text-end fw-bold">
                    {totals.cost.toLocaleString()}{" "}
                    {CURRENCY}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center">
            <div className="small text-muted">
              {msg || "—"}
            </div>
            <button
              className="btn btn-dark"
              type="button"
              onClick={handleReceive}
              disabled={loading || !ship}
            >
              {loading ? (
                <span className="spinner-border spinner-border-sm me-2" />
              ) : null}
              Enregistrer la réception
            </button>
          </div>
        </div>
      </div>

      <p className="text-muted small mt-3">
        Règle : <code>onOrder</code> diminue seulement pour les
        lignes liées à un <strong>PO</strong>. Les lignes hors-PO
        augmentent le stock mais ne touchent pas{" "}
        <code>onOrder</code>. Le statut du Shipment passe à{" "}
        <em>received</em> si tout est reçu, sinon{" "}
        <em>partial</em>. Chaque réception crée aussi un mouvement
        dans <code>inventoryMoves</code> (type{" "}
        <code>reception</code>) pour la fiche de stock.
      </p>
    </div>
  );
}
